import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    Camera,
    CheckCircle2,
    ClipboardCheck,
    ExternalLink,
    MapPin,
    Images,
    SearchCheck,
    ShieldCheck,
} from 'lucide-react';
import Breadcrumbs, { type Crumb } from '../../navigation/breadcrumbs/Breadcrumbs';
import FAQBlock, { type FAQIcon } from '../../content/faq-block/FAQBlock';
import GoogleServiceArea from '../../external/google-service-area/GoogleServiceArea';
import {
    ProjectWorkShowcaseHeroGallery,
    ProjectWorkShowcasePhotoGalleryGrid,
} from './ProjectWorkShowcaseGallery';

export type ProjectWorkShowcaseLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type ProjectShowcaseAction = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    variant?: 'primary' | 'secondary';
};

type ProjectWorkShowcaseIconComponent = React.ComponentType<{
    className?: string;
    size?: number;
    style?: CSSProperties;
    strokeWidth?: number;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

export type ProjectWorkShowcaseIconRenderer = React.ElementType<{
    className?: string;
    style?: CSSProperties;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

const detailIconRegistry = {
    photoGroup: Images,
    service: ClipboardCheck,
    location: MapPin,
    photos: Camera,
} satisfies Record<string, ProjectWorkShowcaseIconComponent>;

export type ProjectWorkShowcaseDetailIconToken = keyof typeof detailIconRegistry;

export const projectWorkShowcaseDetailIconTokens = Object.keys(
    detailIconRegistry,
) as ProjectWorkShowcaseDetailIconToken[];

export type ProjectShowcaseDetail = {
    label: React.ReactNode;
    value: React.ReactNode;
    icon?: React.ReactNode | false;
    iconComponent?: ProjectWorkShowcaseIconRenderer;
    iconToken?: ProjectWorkShowcaseDetailIconToken;
};

export type ProjectShowcaseStat = {
    value: React.ReactNode;
    label: React.ReactNode;
    description?: React.ReactNode;
};

export type ProjectShowcaseService = {
    label: React.ReactNode;
    href: string;
};

export type ProjectShowcaseVideo = {
    src: string;
    poster: string;
    title: React.ReactNode;
    description: React.ReactNode;
};

export type ProjectShowcaseFaq = {
    question: string;
    answer: string;
};

export type ProjectShowcaseLocation = {
    kicker?: React.ReactNode;
    title: React.ReactNode;
    description?: React.ReactNode;
    areaServed?: React.ReactNode;
    locationDetail?: React.ReactNode;
    serviceAreas?: React.ReactNode[];
    serviceAreaLimit?: number;
    mapEmbedUrl?: string;
    mapLink?: string;
    mapTitle?: string;
    note?: React.ReactNode;
    latitude?: number;
    longitude?: number;
};

export type ProjectWorkShowcaseImage = {
    id?: string | number;
    url?: string;
    src?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    caption?: string;
    description?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
};

export type ProjectWorkShowcaseBreadcrumbItem = {
    label: string;
    href?: string;
};

export type ProjectWorkShowcaseProps = {
    LinkComponent: ProjectWorkShowcaseLinkComponent;
    breadcrumbItems: ProjectWorkShowcaseBreadcrumbItem[];
    eyebrow: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
    categoryLabel: React.ReactNode;
    heroImage: ProjectWorkShowcaseImage;
    images: ProjectWorkShowcaseImage[];
    primaryAction: ProjectShowcaseAction;
    secondaryAction: ProjectShowcaseAction;
    overviewTitle: React.ReactNode;
    overviewParagraphs: React.ReactNode[];
    details: ProjectShowcaseDetail[];
    stats: ProjectShowcaseStat[];
    deliveredTitle: React.ReactNode;
    deliveredItems: React.ReactNode[];
    challengesTitle?: React.ReactNode;
    challengesItems?: React.ReactNode[];
    challengesRichText?: React.ReactNode;
    resultsTitle: string;
    results: ProjectShowcaseStat[];
    servicesTitle: React.ReactNode;
    services: ProjectShowcaseService[];
    ctaTitle: React.ReactNode;
    ctaDescription: React.ReactNode;
    ctaPrimaryAction: ProjectShowcaseAction;
    ctaSecondaryAction?: ProjectShowcaseAction;
    video?: ProjectShowcaseVideo;
    faqs?: ProjectShowcaseFaq[];
    faqTitle?: string;
    faqDescription?: string;
    faqIcon?: FAQIcon;
    faqIncludeSchema?: boolean;
    location?: ProjectShowcaseLocation;
    contextKicker?: React.ReactNode;
    evidenceKicker?: React.ReactNode;
    evidenceDescription?: React.ReactNode;
    galleryKicker?: React.ReactNode;
    galleryTitle?: React.ReactNode;
    galleryDescription?: React.ReactNode;
    servicesKicker?: React.ReactNode;
    servicesDescription?: React.ReactNode;
    ctaKicker?: React.ReactNode;
    lightboxLabel?: string;
    imageFallbackAlt?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

const projectWorkShowcaseCss = `
.project-work-showcase {
  --project-work-showcase-red: #b80000;
  --project-work-showcase-red-dark: #8f0000;
  --project-work-showcase-green: #226b2b;
  --project-work-showcase-ink: #151515;
  --project-work-showcase-muted: #5b6475;
  --project-work-showcase-border: rgba(21, 21, 21, 0.12);
  --project-work-showcase-soft: #f5f6f7;
  --project-work-showcase-warm: #fff8ef;
  width: 100%;
  min-width: 0;
  color: var(--project-work-showcase-ink);
  background: #ffffff;
  font-family: inherit;
  box-sizing: border-box;
}

.project-work-showcase,
.project-work-showcase * {
  box-sizing: border-box;
}

.project-work-showcase-shell {
  width: min(100%, 1440px);
  margin: 0 auto;
  padding: 0 32px;
}

.project-work-showcase-hero {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: #ffffff;
}

.project-work-showcase-hero::before,
.project-work-showcase-hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.project-work-showcase-hero::before {
  background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.96) 28%, rgba(255, 255, 255, 0.7) 43%, rgba(255, 255, 255, 0.24) 56%, rgba(255, 255, 255, 0.05) 66%, rgba(255, 255, 255, 0) 74%, rgba(255, 255, 255, 0) 100%);
}

.project-work-showcase-hero::after {
  inset: auto auto 0 0;
  width: min(64vw, 920px);
  height: 118px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 88%);
}

.project-work-showcase-hero .project-work-showcase-shell {
  position: relative;
  z-index: 2;
}

.project-work-showcase-breadcrumb-wrap {
  padding: 22px 0 6px;
}

.project-work-showcase-breadcrumb-wrap nav {
  padding-left: 0;
  padding-right: 0;
  border-radius: 0;
}

.project-work-showcase-hero-grid {
  display: block;
  min-height: 470px;
  padding: 34px 0 52px;
}

.project-work-showcase-hero-content {
  position: relative;
  z-index: 2;
  max-width: 720px;
}

.project-work-showcase-eyebrow,
.project-work-showcase-section-kicker {
  margin: 0 0 12px;
  color: var(--project-work-showcase-red);
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.project-work-showcase-title {
  margin: 0;
  color: #111111;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.07;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.project-work-showcase-description {
  max-width: 610px;
  margin: 16px 0 0;
  color: #4d5668;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.55;
  letter-spacing: 0;
}

.project-work-showcase-hero-actions,
.project-work-showcase-cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 22px;
}

.project-work-showcase-primary-action,
.project-work-showcase-secondary-action {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border-radius: 8px;
  padding: 10px 15px;
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0;
  text-decoration: none;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.project-work-showcase-primary-action {
  border: 2px solid var(--project-work-showcase-red);
  background: var(--project-work-showcase-red);
  color: #ffffff;
}

.project-work-showcase-primary-action:hover,
.project-work-showcase-primary-action:focus-visible {
  border-color: var(--project-work-showcase-red-dark);
  background: var(--project-work-showcase-red-dark);
  transform: translateY(-1px);
}

.project-work-showcase-secondary-action {
  border: 2px solid rgba(184, 0, 0, 0.28);
  background: #ffffff;
  color: var(--project-work-showcase-red);
}

.project-work-showcase-secondary-action:hover,
.project-work-showcase-secondary-action:focus-visible {
  border-color: var(--project-work-showcase-red);
  color: var(--project-work-showcase-red-dark);
  transform: translateY(-1px);
}

.project-work-showcase-search-signals {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  max-width: 660px;
  margin: 24px 0 0;
}

.project-work-showcase-search-signal {
  min-width: 0;
  border: 1px solid var(--project-work-showcase-border);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.68);
  padding: 10px 11px;
  backdrop-filter: blur(12px);
}

.project-work-showcase-search-signal dt {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 6px;
  color: var(--project-work-showcase-red);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.project-work-showcase-search-signal dd {
  margin: 0;
  color: #1f2633;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
}

.project-work-showcase-context-gallery {
  position: relative;
  min-width: 0;
}

.project-work-showcase-hero-backdrop {
  position: absolute;
  inset: 0 0 0 auto;
  z-index: 0;
  width: min(66vw, 1180px);
  margin: 0;
  overflow: hidden;
  background: transparent;
}

.project-work-showcase-hero-backdrop::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.82) 10%, rgba(255, 255, 255, 0.34) 26%, rgba(255, 255, 255, 0.08) 42%, rgba(255, 255, 255, 0) 58%, rgba(255, 255, 255, 0) 100%);
}

.project-work-showcase-hero-backdrop img {
  position: absolute;
  inset: 0 0 0 auto;
  display: block;
  height: 100%;
  width: auto;
  min-height: 100%;
  max-width: none;
  object-fit: contain;
  object-position: var(--project-work-showcase-hero-image-position, right center);
  filter: saturate(1.02) contrast(1.03);
}

.project-work-showcase-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.project-work-showcase-hero-figure {
  position: relative;
  overflow: hidden;
  margin: 0;
  border: 1px solid rgba(21, 21, 21, 0.14);
  border-radius: 8px;
  background: #111111;
  box-shadow: 0 22px 48px rgba(21, 21, 21, 0.14);
}

.project-work-showcase-image-button {
  display: block;
  position: relative;
  width: 100%;
  margin: 0;
  border: 0;
  background: #111111;
  color: inherit;
  cursor: zoom-in;
  font: inherit;
  overflow: hidden;
  padding: 0;
  text-align: inherit;
}

.project-work-showcase-image-button:focus-visible {
  outline: 3px solid rgba(184, 0, 0, 0.36);
  outline-offset: 3px;
}

.project-work-showcase-hero-image-frame,
.project-work-showcase-featured-image-wrap,
.project-work-showcase-gallery-image-wrap {
  display: block;
  position: relative;
  width: 100%;
}

.project-work-showcase-hero-image-frame {
  aspect-ratio: 16 / 10;
}

.project-work-showcase-featured-image-wrap,
.project-work-showcase-gallery-image-wrap {
  aspect-ratio: 4 / 3;
}

.project-work-showcase-hero-image,
.project-work-showcase-featured-image,
.project-work-showcase-gallery-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 260ms ease, filter 260ms ease;
}

.project-work-showcase-image-button:hover .project-work-showcase-hero-image,
.project-work-showcase-image-button:hover .project-work-showcase-featured-image,
.project-work-showcase-image-button:hover .project-work-showcase-gallery-image {
  transform: scale(1.025);
  filter: saturate(1.05);
}

.project-work-showcase-image-expand {
  display: inline-flex;
  position: absolute;
  z-index: 5;
  top: 14px;
  right: 14px;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 999px;
  background: rgba(17, 17, 17, 0.72);
  color: #ffffff;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.2);
  opacity: 0.94;
  transition: background-color 160ms ease, transform 160ms ease, opacity 160ms ease;
}

.project-work-showcase-image-button:hover .project-work-showcase-image-expand,
.project-work-showcase-image-button:focus-visible .project-work-showcase-image-expand {
  background: var(--project-work-showcase-red);
  opacity: 1;
  transform: translateY(-1px);
}

.project-work-showcase-hero-figure figcaption {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  border-radius: 8px;
  background: rgba(17, 17, 17, 0.76);
  color: #ffffff;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
}

.project-work-showcase-featured-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.project-work-showcase-featured-image-frame {
  position: relative;
  overflow: hidden;
  margin: 0;
  border: 1px solid rgba(21, 21, 21, 0.12);
  border-radius: 8px;
  background: #ffffff;
}

.project-work-showcase-featured-caption {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.project-work-showcase-featured-image-frame .project-work-showcase-image-expand,
.project-work-showcase-gallery-item .project-work-showcase-image-expand {
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
}

.project-work-showcase-context-band,
.project-work-showcase-gallery-band,
.project-work-showcase-services-band,
.project-work-showcase-location-band,
.project-work-showcase-faq-band {
  background: #ffffff;
  padding: 72px 0;
}

.project-work-showcase-context-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.88fr) minmax(420px, 0.76fr);
  gap: 44px;
  align-items: start;
}

.project-work-showcase-services-grid,
.project-work-showcase-location-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 0.65fr);
  gap: 36px;
  align-items: start;
}

.project-work-showcase-context-copy h2,
.project-work-showcase-evidence-header h2,
.project-work-showcase-gallery-header h2,
.project-work-showcase-services-copy h2,
.project-work-showcase-location-copy h2,
.project-work-showcase-video-grid h2,
.project-work-showcase-cta h2 {
  margin: 0;
  color: #111111;
  font-size: 36px;
  font-weight: 900;
  line-height: 1.14;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.project-work-showcase-context-copy p,
.project-work-showcase-evidence-header p,
.project-work-showcase-gallery-header p,
.project-work-showcase-services-copy p,
.project-work-showcase-location-copy p,
.project-work-showcase-video-grid p,
.project-work-showcase-cta p {
  color: var(--project-work-showcase-muted);
  font-size: 17px;
  font-weight: 650;
  line-height: 1.65;
  letter-spacing: 0;
}

.project-work-showcase-context-copy p {
  max-width: 860px;
  margin: 18px 0 0;
}

.project-work-showcase-detail-panel {
  display: grid;
  gap: 12px;
}

.project-work-showcase-context-details {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 24px;
}

.project-work-showcase-detail-item {
  border-left: 4px solid var(--project-work-showcase-red);
  border-radius: 8px;
  background: var(--project-work-showcase-soft);
  padding: 16px 18px;
}

.project-work-showcase-context-details .project-work-showcase-detail-item {
  border-left-width: 3px;
  padding: 12px 13px;
}

.project-work-showcase-detail-item--with-icon {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.project-work-showcase-context-details .project-work-showcase-detail-item--with-icon {
  gap: 10px;
}

.project-work-showcase-detail-icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(184, 0, 0, 0.16);
  border-radius: 999px;
  background: rgba(184, 0, 0, 0.08);
  color: var(--project-work-showcase-red);
}

.project-work-showcase-context-details .project-work-showcase-detail-icon {
  width: 30px;
  height: 30px;
}

.project-work-showcase-detail-icon svg {
  display: block;
  width: 19px;
  height: 19px;
  stroke-width: 2.4;
}

.project-work-showcase-context-details .project-work-showcase-detail-icon svg {
  width: 16px;
  height: 16px;
}

.project-work-showcase-detail-item--with-icon .project-work-showcase-detail-icon {
  grid-row: 1 / span 2;
}

.project-work-showcase-detail-item--with-icon dt,
.project-work-showcase-detail-item--with-icon dd {
  grid-column: 2;
}

.project-work-showcase-detail-item dt {
  margin: 0 0 6px;
  color: var(--project-work-showcase-red);
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.project-work-showcase-context-details .project-work-showcase-detail-item dt {
  margin-bottom: 4px;
  font-size: 11px;
}

.project-work-showcase-detail-item dd {
  margin: 0;
  color: #202634;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.35;
}

.project-work-showcase-context-details .project-work-showcase-detail-item dd {
  font-size: 14px;
  line-height: 1.32;
}

.project-work-showcase-evidence-band {
  background: var(--project-work-showcase-soft);
  padding: 72px 0;
}

.project-work-showcase-evidence-header,
.project-work-showcase-gallery-header {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(320px, 0.55fr);
  gap: 36px;
  align-items: end;
  margin-bottom: 30px;
}

.project-work-showcase-evidence-header p,
.project-work-showcase-gallery-header p {
  margin: 0;
}

.project-work-showcase-evidence-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.78fr);
  gap: 24px;
  align-items: start;
}

.project-work-showcase-check-list {
  display: grid;
  gap: 12px;
}

.project-work-showcase-check-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  border: 1px solid rgba(21, 21, 21, 0.1);
  border-radius: 8px;
  background: #ffffff;
  padding: 18px;
  color: #202634;
  font-size: 16px;
  font-weight: 850;
  line-height: 1.45;
}

.project-work-showcase-check-item svg {
  color: var(--project-work-showcase-green);
}

.project-work-showcase-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.project-work-showcase-stat-card {
  border: 1px solid rgba(184, 0, 0, 0.14);
  border-radius: 8px;
  background: #ffffff;
  padding: 18px;
}

.project-work-showcase-stat-card strong {
  display: block;
  color: var(--project-work-showcase-red);
  font-size: 38px;
  font-weight: 900;
  line-height: 1;
}

.project-work-showcase-stat-card span {
  display: block;
  margin-top: 8px;
  color: #1f2633;
  font-size: 15px;
  font-weight: 900;
}

.project-work-showcase-stat-card p {
  margin: 10px 0 0;
  color: var(--project-work-showcase-muted);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
}

.project-work-showcase-challenges-panel {
  border: 1px solid rgba(184, 0, 0, 0.14);
  border-radius: 8px;
  background: #ffffff;
  padding: 22px;
}

.project-work-showcase-challenges-panel h3 {
  margin: 0;
  color: #111111;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.18;
  letter-spacing: 0;
}

.project-work-showcase-challenge-list {
  display: grid;
  gap: 10px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.project-work-showcase-challenge-list li {
  position: relative;
  padding-left: 18px;
  color: #202634;
  font-size: 15px;
  font-weight: 850;
  line-height: 1.45;
}

.project-work-showcase-challenge-list li::before {
  content: "";
  position: absolute;
  top: 0.62em;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--project-work-showcase-red);
}

.project-work-showcase-challenge-rich-text {
  margin-top: 20px;
  border-top: 1px solid rgba(21, 21, 21, 0.1);
  padding-top: 18px;
  color: var(--project-work-showcase-muted);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.62;
}

.project-work-showcase-challenge-rich-text p,
.project-work-showcase-challenge-rich-text ul,
.project-work-showcase-challenge-rich-text ol {
  margin: 0 0 12px;
}

.project-work-showcase-challenge-rich-text :last-child {
  margin-bottom: 0;
}

.project-work-showcase-challenge-rich-text a {
  color: var(--project-work-showcase-red);
  font-weight: 900;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.project-work-showcase-challenge-rich-text a:hover,
.project-work-showcase-challenge-rich-text a:focus-visible {
  color: var(--project-work-showcase-red-dark);
}

.project-work-showcase-gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.project-work-showcase-gallery-item {
  overflow: hidden;
  margin: 0;
  border: 1px solid var(--project-work-showcase-border);
  border-radius: 8px;
  background: #ffffff;
}

.project-work-showcase-gallery-item figcaption {
  padding: 14px 16px 16px;
  color: #303746;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.45;
}

.project-work-showcase-lightbox {
  --yarl__slide_captions_container_background: rgba(17, 17, 17, 0.84);
  --yarl__slide_captions_container_padding: 14px 18px;
  --yarl__slide_title_color: #ffffff;
  --yarl__slide_title_font_size: 16px;
  --yarl__slide_title_font_weight: 700;
  --yarl__slide_description_color: rgba(255, 255, 255, 0.9);
  --yarl__slide_description_text_align: start;
}

.project-work-showcase-lightbox .yarl__slide_captions_container {
  font-family: inherit;
}

.project-work-showcase-lightbox .yarl__slide_description {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.project-work-showcase-video-band {
  background: #111111;
  padding: 72px 0;
}

.project-work-showcase-video-grid {
  display: grid;
  grid-template-columns: minmax(280px, 0.45fr) minmax(0, 1fr);
  gap: 34px;
  align-items: center;
}

.project-work-showcase-video-grid h2,
.project-work-showcase-video-grid p {
  color: #ffffff;
}

.project-work-showcase-video-grid p {
  color: rgba(255, 255, 255, 0.78);
}

.project-work-showcase-video {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: #000000;
}

.project-work-showcase-service-links {
  display: grid;
  gap: 12px;
}

.project-work-showcase-service-link {
  display: flex;
  min-height: 62px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border: 1px solid rgba(184, 0, 0, 0.16);
  border-radius: 8px;
  background: var(--project-work-showcase-warm);
  color: #202634;
  padding: 16px 18px;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.3;
  letter-spacing: 0;
  text-decoration: none;
}

.project-work-showcase-service-link span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.project-work-showcase-service-link svg {
  flex: 0 0 auto;
  color: var(--project-work-showcase-red);
}

.project-work-showcase-service-link:hover,
.project-work-showcase-service-link:focus-visible {
  border-color: var(--project-work-showcase-red);
  background: #fff2e4;
  color: var(--project-work-showcase-red-dark);
}

.project-work-showcase-location-band {
  border-top: 1px solid var(--project-work-showcase-border);
  background: linear-gradient(90deg, #ffffff 0%, #ffffff 48%, rgba(245, 246, 247, 0.82) 100%);
}

.project-work-showcase-location-grid {
  grid-template-columns: minmax(0, 0.85fr) minmax(420px, 0.75fr);
  align-items: stretch;
}

.project-work-showcase-location-copy p {
  max-width: 780px;
  margin: 18px 0 0;
}

.project-work-showcase-location-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 24px 0 0;
}

.project-work-showcase-location-fact {
  border-left: 4px solid var(--project-work-showcase-red);
  border-radius: 8px;
  background: var(--project-work-showcase-soft);
  padding: 15px 16px;
}

.project-work-showcase-location-fact dt {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 7px;
  color: var(--project-work-showcase-red);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.project-work-showcase-location-fact dd {
  margin: 0;
  color: #202634;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.35;
}

.project-work-showcase-location-areas {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}

.project-work-showcase-location-areas li {
  border: 1px solid rgba(184, 0, 0, 0.16);
  border-radius: 999px;
  background: #ffffff;
  color: #202634;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 850;
  line-height: 1;
}

.project-work-showcase-location-note {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  margin: 22px 0 0;
  color: var(--project-work-showcase-muted);
  font-size: 14px;
  font-weight: 750;
  line-height: 1.5;
}

.project-work-showcase-location-note svg {
  flex: 0 0 auto;
  color: var(--project-work-showcase-red);
}

.project-work-showcase-map-panel {
  display: grid;
  min-width: 0;
  gap: 14px;
  align-content: start;
}

.project-work-showcase-map-frame {
  overflow: hidden;
  width: 100%;
  border: 1px solid rgba(21, 21, 21, 0.12);
  border-radius: 8px;
  background: #e8ecef;
  box-shadow: 0 18px 36px rgba(21, 21, 21, 0.1);
}

.project-work-showcase-map-aspect {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
}

.project-work-showcase-map-iframe {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.project-work-showcase-map-placeholder {
  display: grid;
  min-height: 320px;
  place-items: center;
  border: 1px solid rgba(21, 21, 21, 0.12);
  border-radius: 8px;
  background: var(--project-work-showcase-soft);
  color: var(--project-work-showcase-muted);
  padding: 24px;
  text-align: center;
}

.project-work-showcase-map-placeholder svg {
  margin-bottom: 12px;
  color: var(--project-work-showcase-red);
}

.project-work-showcase-map-link {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(184, 0, 0, 0.18);
  border-radius: 8px;
  background: #ffffff;
  color: var(--project-work-showcase-red);
  padding: 11px 13px;
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
  text-decoration: none;
}

.project-work-showcase-map-link:hover,
.project-work-showcase-map-link:focus-visible {
  border-color: var(--project-work-showcase-red);
  color: var(--project-work-showcase-red-dark);
}

.project-work-showcase-faq-band {
  border-top: 1px solid var(--project-work-showcase-border);
}

.project-work-showcase-faq-panel > section {
  margin-top: 0;
  border: 1px solid rgba(184, 0, 0, 0.12);
  border-radius: 8px;
  background: var(--project-work-showcase-soft);
}

.project-work-showcase-faq-panel h2 {
  color: #111111;
  font-size: 30px;
  font-weight: 900;
  line-height: 1.14;
  letter-spacing: 0;
}

.project-work-showcase-faq-panel header svg,
.project-work-showcase-faq-panel summary svg {
  color: var(--project-work-showcase-red);
}

.project-work-showcase-cta-band {
  background: var(--project-work-showcase-soft);
  padding: 72px 0 86px;
}

.project-work-showcase-cta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: center;
  border: 1px solid rgba(184, 0, 0, 0.14);
  border-radius: 8px;
  background: #ffffff;
  padding: 30px;
}

.project-work-showcase-cta-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  color: var(--project-work-showcase-red);
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.project-work-showcase-cta p {
  max-width: 800px;
  margin: 14px 0 0;
}

.project-work-showcase-cta-actions {
  justify-content: flex-end;
  margin-top: 0;
}

@media (max-width: 1180px) {
  .project-work-showcase-hero-grid,
  .project-work-showcase-context-grid,
  .project-work-showcase-services-grid,
  .project-work-showcase-location-grid,
  .project-work-showcase-evidence-grid,
  .project-work-showcase-video-grid,
  .project-work-showcase-cta {
    grid-template-columns: 1fr;
  }

  .project-work-showcase-hero-grid {
    min-height: 440px;
    padding: 32px 0 50px;
  }

  .project-work-showcase-hero::before {
    background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.96) 34%, rgba(255, 255, 255, 0.68) 56%, rgba(255, 255, 255, 0.2) 78%, rgba(255, 255, 255, 0) 100%);
  }

  .project-work-showcase-hero::after {
    width: min(78vw, 820px);
  }

  .project-work-showcase-hero-backdrop {
    width: min(74vw, 980px);
  }

  .project-work-showcase-title {
    font-size: 40px;
  }

  .project-work-showcase-search-signals {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .project-work-showcase-evidence-header,
  .project-work-showcase-gallery-header {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .project-work-showcase-cta-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 760px) {
  .project-work-showcase-shell {
    padding: 0 18px;
  }

  .project-work-showcase-breadcrumb-wrap {
    padding-top: 24px;
  }

  .project-work-showcase-hero::before {
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.9) 48%, #ffffff 100%),
      linear-gradient(90deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.86) 62%, rgba(255, 255, 255, 0.36) 100%);
  }

  .project-work-showcase-hero::after {
    inset: auto 0 0;
    width: auto;
    height: 150px;
  }

  .project-work-showcase-hero-backdrop {
    width: 88vw;
  }

  .project-work-showcase-hero-backdrop::before {
    background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.98) 20%, rgba(255, 255, 255, 0.76) 48%, rgba(255, 255, 255, 0.28) 74%, rgba(255, 255, 255, 0) 100%);
  }

  .project-work-showcase-hero-backdrop img {
    object-position: var(--project-work-showcase-hero-image-position, center top);
  }

  .project-work-showcase-hero-grid {
    min-height: 0;
    padding: 34px 0 58px;
  }

  .project-work-showcase-title {
    font-size: 36px;
    line-height: 1.08;
  }

  .project-work-showcase-description {
    margin-top: 18px;
    font-size: 16px;
  }

  .project-work-showcase-hero-actions,
  .project-work-showcase-cta-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .project-work-showcase-primary-action,
  .project-work-showcase-secondary-action {
    width: 100%;
    min-height: 52px;
    padding: 15px 18px;
    font-size: 15px;
  }

  .project-work-showcase-search-signals,
  .project-work-showcase-featured-strip,
  .project-work-showcase-context-details,
  .project-work-showcase-stats-grid,
  .project-work-showcase-gallery-grid {
    grid-template-columns: 1fr;
  }

  .project-work-showcase-hero-figure figcaption {
    position: static;
    border-radius: 0;
    background: #ffffff;
    color: #303746;
    padding: 12px 14px 14px;
    font-size: 14px;
    font-weight: 650;
    line-height: 1.45;
  }

  .project-work-showcase-featured-caption {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    clip-path: none;
    white-space: normal;
    padding: 12px 14px 14px;
    color: #303746;
    font-size: 14px;
    font-weight: 650;
    line-height: 1.45;
  }

  .project-work-showcase-gallery-item figcaption {
    padding: 12px 14px 14px;
    font-size: 14px;
    font-weight: 650;
    line-height: 1.45;
  }

  .project-work-showcase-lightbox {
    --yarl__slide_captions_container_padding: 12px 14px;
    --yarl__slide_title_font_size: 14px;
    --yarl__slide_title_font_weight: 650;
  }

  .project-work-showcase-lightbox .yarl__slide_description {
    font-size: 13px;
  }

  .project-work-showcase-context-band,
  .project-work-showcase-evidence-band,
  .project-work-showcase-gallery-band,
  .project-work-showcase-services-band,
  .project-work-showcase-location-band,
  .project-work-showcase-faq-band,
  .project-work-showcase-video-band,
  .project-work-showcase-cta-band {
    padding: 50px 0;
  }

  .project-work-showcase-context-copy h2,
  .project-work-showcase-evidence-header h2,
  .project-work-showcase-gallery-header h2,
  .project-work-showcase-services-copy h2,
  .project-work-showcase-location-copy h2,
  .project-work-showcase-faq-panel h2,
  .project-work-showcase-video-grid h2,
  .project-work-showcase-cta h2 {
    font-size: 29px;
  }

  .project-work-showcase-context-copy p,
  .project-work-showcase-evidence-header p,
  .project-work-showcase-gallery-header p,
  .project-work-showcase-services-copy p,
  .project-work-showcase-location-copy p,
  .project-work-showcase-video-grid p,
  .project-work-showcase-cta p {
    font-size: 16px;
  }

  .project-work-showcase-location-facts {
    grid-template-columns: 1fr;
  }

  .project-work-showcase-cta {
    padding: 22px;
  }

  .project-work-showcase-challenges-panel {
    padding: 18px;
  }
}
`;

const DefaultLink: ProjectWorkShowcaseLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const getImageSrc = (image?: ProjectWorkShowcaseImage) =>
    image?.url || image?.src || image?.original || image?.thumbnail || '';

const getImageAlt = (image: ProjectWorkShowcaseImage, fallback: string) =>
    image.alt || image.title || fallback;

const getImageTitle = (image: ProjectWorkShowcaseImage, fallback: string) =>
    image.caption || image.title || image.description || getImageAlt(image, fallback);

const getImageKey = (image: ProjectWorkShowcaseImage, index: number) =>
    String(image.id || getImageSrc(image) || `${getImageTitle(image, 'Project photo')}-${index}`);

const getDetailIconToken = (
    detail: ProjectShowcaseDetail,
): ProjectWorkShowcaseDetailIconToken | undefined => {
    if (detail.iconToken) return detail.iconToken;

    const label = String(detail.label).toLowerCase();

    if (label.includes('photo group')) return 'photoGroup';
    if (label.includes('service')) return 'service';
    if (label.includes('location') || label.includes('suburb')) return 'location';
    if (label.includes('media') || label.includes('photo')) return 'photos';

    return undefined;
};

const hasDetailIcon = (detail: ProjectShowcaseDetail) =>
    detail.icon !== false && !!(detail.icon || detail.iconComponent || getDetailIconToken(detail));

const uniqueImages = (images: ProjectWorkShowcaseImage[]) => {
    const seen = new Set<string>();

    return images.filter((image, index) => {
        const src = getImageSrc(image);
        if (!src) return false;

        const key = src || getImageKey(image, index);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
    });
};

function ProjectAction({
    action,
    LinkComponent,
}: {
    action: ProjectShowcaseAction;
    LinkComponent: ProjectWorkShowcaseLinkComponent;
}) {
    const className =
        action.variant === 'secondary'
            ? 'project-work-showcase-secondary-action'
            : 'project-work-showcase-primary-action';

    return (
        <LinkComponent
            href={action.href}
            className={className}
            aria-label={action.ariaLabel || String(action.label)}
        >
            <span>{action.label}</span>
            <ArrowRight size={20} aria-hidden="true" />
        </LinkComponent>
    );
}

function ProjectHeroImage({
    image,
    imageFallbackAlt,
}: {
    image: ProjectWorkShowcaseImage;
    imageFallbackAlt: string;
}) {
    const src = getImageSrc(image);
    const alt = getImageAlt(image, imageFallbackAlt);
    const objectPosition = image.objectPosition
        ? { objectPosition: image.objectPosition }
        : undefined;

    if (!src) return null;

    return (
        <figure className="project-work-showcase-hero-backdrop">
            <img
                src={src}
                alt={alt}
                title={image.title}
                loading="eager"
                decoding="async"
                style={objectPosition}
            />
            <figcaption className="project-work-showcase-visually-hidden">
                {getImageTitle(image, imageFallbackAlt)}
            </figcaption>
        </figure>
    );
}

function ProjectDetailIcon({ detail }: { detail: ProjectShowcaseDetail }) {
    if (detail.icon === false) return null;

    if (detail.icon) {
        return (
            <span className="project-work-showcase-detail-icon" aria-hidden="true">
                {detail.icon}
            </span>
        );
    }

    if (detail.iconComponent) {
        const IconComponent = detail.iconComponent;
        return (
            <span className="project-work-showcase-detail-icon" aria-hidden="true">
                <IconComponent aria-hidden={true} focusable={false} />
            </span>
        );
    }

    const iconToken = getDetailIconToken(detail);
    if (!iconToken) return null;

    const Icon = detailIconRegistry[iconToken];

    return (
        <span className="project-work-showcase-detail-icon" aria-hidden="true">
            <Icon size={19} strokeWidth={2.4} aria-hidden="true" />
        </span>
    );
}

function ProjectLocationSection({
    location,
    LinkComponent,
}: {
    location?: ProjectShowcaseLocation;
    LinkComponent: ProjectWorkShowcaseLinkComponent;
}) {
    if (!location) return null;

    const serviceAreaLimit =
        typeof location.serviceAreaLimit === 'number' ? Math.max(0, location.serviceAreaLimit) : 4;
    const serviceAreas = (location.serviceAreas || []).filter(Boolean).slice(0, serviceAreaLimit);
    const hasCoordinates =
        typeof location.latitude === 'number' && typeof location.longitude === 'number';
    const locationTitle = typeof location.title === 'string' ? location.title : 'Project location';
    const mapTitle = location.mapTitle || `${locationTitle} map`;

    return (
        <section className="project-work-showcase-location-band" id="project-location">
            <div className="project-work-showcase-shell">
                <div className="project-work-showcase-location-grid">
                    <div className="project-work-showcase-location-copy">
                        <p className="project-work-showcase-section-kicker">
                            {location.kicker || 'Location context'}
                        </p>
                        <h2>{location.title}</h2>
                        {location.description ? <p>{location.description}</p> : null}

                        <dl
                            className="project-work-showcase-location-facts"
                            aria-label="Project location details"
                        >
                            {location.areaServed ? (
                                <div className="project-work-showcase-location-fact">
                                    <dt>
                                        <MapPin size={16} aria-hidden="true" />
                                        Area served
                                    </dt>
                                    <dd>{location.areaServed}</dd>
                                </div>
                            ) : null}

                            {location.locationDetail ? (
                                <div className="project-work-showcase-location-fact">
                                    <dt>
                                        <ShieldCheck size={16} aria-hidden="true" />
                                        Project location
                                    </dt>
                                    <dd>{location.locationDetail}</dd>
                                </div>
                            ) : null}

                            {hasCoordinates ? (
                                <div className="project-work-showcase-location-fact">
                                    <dt>
                                        <SearchCheck size={16} aria-hidden="true" />
                                        Map centre
                                    </dt>
                                    <dd>
                                        {location.latitude}, {location.longitude}
                                    </dd>
                                </div>
                            ) : null}
                        </dl>

                        {serviceAreas.length ? (
                            <ul
                                className="project-work-showcase-location-areas"
                                aria-label="Nearby service areas"
                            >
                                {serviceAreas.map((area, index) => (
                                    <li key={`${String(area)}-${index}`}>{area}</li>
                                ))}
                            </ul>
                        ) : null}

                        {location.note ? (
                            <p className="project-work-showcase-location-note">
                                <MapPin size={17} aria-hidden="true" />
                                <span>{location.note}</span>
                            </p>
                        ) : null}
                    </div>

                    <div className="project-work-showcase-map-panel">
                        {location.mapEmbedUrl ? (
                            <GoogleServiceArea
                                googleDetails={{ googleServiceArea: location.mapEmbedUrl }}
                                title={mapTitle}
                                className="project-work-showcase-map-frame"
                                frameClassName="project-work-showcase-map-aspect"
                                iframeClassName="project-work-showcase-map-iframe"
                            />
                        ) : (
                            <div
                                className="project-work-showcase-map-placeholder"
                                role="img"
                                aria-label={mapTitle}
                            >
                                <MapPin size={28} aria-hidden="true" />
                                <span>
                                    {location.areaServed ||
                                        location.locationDetail ||
                                        'Project service area'}
                                </span>
                            </div>
                        )}

                        {location.mapLink ? (
                            <LinkComponent
                                href={location.mapLink}
                                className="project-work-showcase-map-link"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open this service area in Google Maps"
                            >
                                <span>Open map</span>
                                <ExternalLink size={16} aria-hidden="true" />
                            </LinkComponent>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function ProjectWorkShowcase({
    LinkComponent = DefaultLink,
    breadcrumbItems,
    eyebrow,
    title,
    description,
    categoryLabel,
    heroImage,
    images,
    primaryAction,
    secondaryAction,
    overviewTitle,
    overviewParagraphs,
    details,
    stats,
    deliveredTitle,
    deliveredItems,
    challengesTitle = 'Project challenges',
    challengesItems,
    challengesRichText,
    resultsTitle,
    results,
    servicesTitle,
    services,
    ctaTitle,
    ctaDescription,
    ctaPrimaryAction,
    ctaSecondaryAction,
    video,
    faqs,
    faqTitle = 'Project FAQs',
    faqDescription,
    faqIcon = 'help',
    faqIncludeSchema = true,
    location,
    contextKicker = 'Project context',
    evidenceKicker = 'Real work evidence',
    evidenceDescription = 'These photos are presented as job-based evidence, with service links and captions kept close to the media so visitors can understand what the work relates to.',
    galleryKicker = 'Photo gallery',
    galleryTitle = 'Approved project photos',
    galleryDescription = 'The gallery keeps the client work visible beyond the hero image, which helps the page feel specific to the job instead of templated.',
    servicesKicker = 'Service context',
    servicesDescription = 'The project is connected to the most relevant service pages so the visual proof, page copy and structured data all describe the same type of work.',
    ctaKicker = 'Sydney termite and pest control',
    lightboxLabel = 'Project photo gallery',
    imageFallbackAlt = 'Project photo',
    className,
    style,
    ariaLabel,
}: ProjectWorkShowcaseProps) {
    const galleryImages = uniqueImages([heroImage, ...(images || [])]);
    const mediaStat = stats[0];
    const serviceStat = stats[1];
    const projectChallengeItems = (challengesItems || []).filter(Boolean);
    const hasChallengeContent = projectChallengeItems.length > 0 || !!challengesRichText;
    const challengesAriaLabel =
        typeof challengesTitle === 'string' ? challengesTitle : 'Project challenges';
    const rootClassName = ['project-work-showcase', className].filter(Boolean).join(' ');
    const crumbs: Crumb[] = breadcrumbItems.map((item) => ({
        title: item.label,
        href: item.href,
    }));

    return (
        <div className={rootClassName} style={style} aria-label={ariaLabel || String(title)}>
            <style>{projectWorkShowcaseCss}</style>
            <section className="project-work-showcase-hero">
                <ProjectHeroImage image={heroImage} imageFallbackAlt={imageFallbackAlt} />

                <div className="project-work-showcase-shell">
                    <div className="project-work-showcase-breadcrumb-wrap">
                        <Breadcrumbs
                            crumbs={crumbs}
                            LinkComponent={LinkComponent}
                            showHomeIcon={false}
                            homeAccent="var(--project-work-showcase-red)"
                            currentPageAccent="var(--project-work-showcase-ink)"
                        />
                    </div>

                    <div className="project-work-showcase-hero-grid">
                        <div className="project-work-showcase-hero-content">
                            <p className="project-work-showcase-eyebrow">{eyebrow}</p>
                            <h1 className="project-work-showcase-title">{title}</h1>
                            <p className="project-work-showcase-description">{description}</p>

                            <div className="project-work-showcase-hero-actions">
                                <ProjectAction
                                    action={primaryAction}
                                    LinkComponent={LinkComponent}
                                />
                                <ProjectAction
                                    action={secondaryAction}
                                    LinkComponent={LinkComponent}
                                />
                            </div>

                            <dl
                                className="project-work-showcase-search-signals"
                                aria-label="Project service context"
                            >
                                <div className="project-work-showcase-search-signal">
                                    <dt>
                                        <ShieldCheck size={18} aria-hidden="true" />
                                        Service type
                                    </dt>
                                    <dd>{categoryLabel}</dd>
                                </div>
                                <div className="project-work-showcase-search-signal">
                                    <dt>
                                        <Camera size={18} aria-hidden="true" />
                                        Client media
                                    </dt>
                                    <dd>
                                        {mediaStat
                                            ? `${String(mediaStat.value)} ${String(mediaStat.label)}`
                                            : `${galleryImages.length} approved photos`}
                                    </dd>
                                </div>
                                <div className="project-work-showcase-search-signal">
                                    <dt>
                                        <SearchCheck size={18} aria-hidden="true" />
                                        Related pages
                                    </dt>
                                    <dd>
                                        {serviceStat
                                            ? `${String(serviceStat.value)} ${String(serviceStat.label)}`
                                            : `${services.length} service links`}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
            </section>

            <section className="project-work-showcase-context-band">
                <div className="project-work-showcase-shell">
                    <div className="project-work-showcase-context-grid">
                        <div className="project-work-showcase-context-copy">
                            <p className="project-work-showcase-section-kicker">{contextKicker}</p>
                            <h2>{overviewTitle}</h2>
                            {overviewParagraphs.map((paragraph, index) => (
                                <p key={index}>{paragraph}</p>
                            ))}

                            <dl
                                className="project-work-showcase-detail-panel project-work-showcase-context-details"
                                aria-label="Project details"
                            >
                                {details.map((detail, index) => {
                                    const detailItemClassName = [
                                        'project-work-showcase-detail-item',
                                        hasDetailIcon(detail)
                                            ? 'project-work-showcase-detail-item--with-icon'
                                            : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ');

                                    return (
                                        <div
                                            className={detailItemClassName}
                                            key={`${String(detail.label)}-${index}`}
                                        >
                                            <ProjectDetailIcon detail={detail} />
                                            <dt>{detail.label}</dt>
                                            <dd>{detail.value}</dd>
                                        </div>
                                    );
                                })}
                            </dl>
                        </div>

                        <div
                            className="project-work-showcase-context-gallery"
                            aria-label="Featured project photos"
                        >
                            <ProjectWorkShowcaseHeroGallery
                                heroImage={heroImage}
                                images={images}
                                imageFallbackAlt={imageFallbackAlt}
                                lightboxLabel={lightboxLabel}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="project-work-showcase-evidence-band">
                <div className="project-work-showcase-shell">
                    <div className="project-work-showcase-evidence-header">
                        <div>
                            <p className="project-work-showcase-section-kicker">{evidenceKicker}</p>
                            <h2>{deliveredTitle}</h2>
                        </div>
                        <p>{evidenceDescription}</p>
                    </div>

                    <div className="project-work-showcase-evidence-grid">
                        <div className="project-work-showcase-check-list">
                            {deliveredItems.map((item, index) => (
                                <div className="project-work-showcase-check-item" key={index}>
                                    <CheckCircle2 size={22} aria-hidden="true" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>

                        {hasChallengeContent ? (
                            <article
                                className="project-work-showcase-challenges-panel"
                                aria-label={challengesAriaLabel}
                            >
                                <h3>{challengesTitle}</h3>

                                {projectChallengeItems.length ? (
                                    <ul className="project-work-showcase-challenge-list">
                                        {projectChallengeItems.map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                    </ul>
                                ) : null}

                                {challengesRichText ? (
                                    <div className="project-work-showcase-challenge-rich-text">
                                        {challengesRichText}
                                    </div>
                                ) : null}
                            </article>
                        ) : (
                            <div
                                className="project-work-showcase-stats-grid"
                                aria-label={resultsTitle}
                            >
                                {results.map((result, index) => (
                                    <article
                                        className="project-work-showcase-stat-card"
                                        key={`${String(result.value)}-${index}`}
                                    >
                                        <strong>{result.value}</strong>
                                        <span>{result.label}</span>
                                        {result.description ? <p>{result.description}</p> : null}
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="project-work-showcase-gallery-band" id="project-photos">
                <div className="project-work-showcase-shell">
                    <div className="project-work-showcase-gallery-header">
                        <div>
                            <p className="project-work-showcase-section-kicker">{galleryKicker}</p>
                            <h2>{galleryTitle}</h2>
                        </div>
                        <p>{galleryDescription}</p>
                    </div>

                    <ProjectWorkShowcasePhotoGalleryGrid
                        heroImage={heroImage}
                        images={images}
                        imageFallbackAlt={imageFallbackAlt}
                        lightboxLabel={lightboxLabel}
                    />
                </div>
            </section>

            {video ? (
                <section className="project-work-showcase-video-band">
                    <div className="project-work-showcase-shell">
                        <div className="project-work-showcase-video-grid">
                            <div>
                                <p className="project-work-showcase-section-kicker">
                                    Project video
                                </p>
                                <h2>{video.title}</h2>
                                <p>{video.description}</p>
                            </div>
                            <video
                                className="project-work-showcase-video"
                                controls
                                playsInline
                                preload="metadata"
                                poster={video.poster}
                            >
                                <source src={video.src} type="video/mp4" />
                            </video>
                        </div>
                    </div>
                </section>
            ) : null}

            <section className="project-work-showcase-services-band">
                <div className="project-work-showcase-shell">
                    <div className="project-work-showcase-services-grid">
                        <div className="project-work-showcase-services-copy">
                            <p className="project-work-showcase-section-kicker">{servicesKicker}</p>
                            <h2>{servicesTitle}</h2>
                            <p>{servicesDescription}</p>
                        </div>

                        <div className="project-work-showcase-service-links">
                            {services.map((service) => (
                                <LinkComponent
                                    href={service.href}
                                    className="project-work-showcase-service-link"
                                    key={service.href}
                                >
                                    <span>
                                        <ClipboardCheck size={20} aria-hidden="true" />
                                        {service.label}
                                    </span>
                                    <ExternalLink size={18} aria-hidden="true" />
                                </LinkComponent>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <ProjectLocationSection location={location} LinkComponent={LinkComponent} />

            {faqs?.length ? (
                <section className="project-work-showcase-faq-band">
                    <div className="project-work-showcase-shell">
                        <div className="project-work-showcase-faq-panel">
                            <FAQBlock
                                title={faqTitle}
                                description={faqDescription}
                                items={faqs}
                                icon={faqIcon}
                                includeSchema={faqIncludeSchema}
                            />
                        </div>
                    </div>
                </section>
            ) : null}

            <section className="project-work-showcase-cta-band">
                <div className="project-work-showcase-shell">
                    <div className="project-work-showcase-cta">
                        <div>
                            <p className="project-work-showcase-cta-kicker">
                                <MapPin size={18} aria-hidden="true" />
                                {ctaKicker}
                            </p>
                            <h2>{ctaTitle}</h2>
                            <p>{ctaDescription}</p>
                        </div>
                        <div className="project-work-showcase-cta-actions">
                            <ProjectAction
                                action={ctaPrimaryAction}
                                LinkComponent={LinkComponent}
                            />
                            {ctaSecondaryAction ? (
                                <ProjectAction
                                    action={ctaSecondaryAction}
                                    LinkComponent={LinkComponent}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
